import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
  fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52',
  outline: 'none', colorScheme: 'light', boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block', fontFamily: "'Inter', sans-serif", fontSize: 9,
  fontWeight: 500, letterSpacing: '0.2em', textTransform: 'uppercase',
  color: '#6b6a5c', marginBottom: 6,
};

interface Warehouse {
  id: string;
  name: string;
  code: string;
}

interface ReceiveStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  product: {
    id: string;
    name: string;
    sku: string;
    isSerialized: boolean;
    costPrice?: number;
  };
}

const ReceiveStockModal: React.FC<ReceiveStockModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  product,
}) => {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    warehouseId: '',
    quantity: 1,
    unitCost: product.costPrice || 0,
    referenceType: '',
    referenceId: '',
    notes: '',
  });

  const [serialNumbers, setSerialNumbers] = useState<string[]>(['']);

  useEffect(() => {
    if (isOpen) fetchWarehouses();
  }, [isOpen]);

  useEffect(() => {
    if (product.isSerialized) {
      const newSerialNumbers = Array(formData.quantity).fill('').map((_, i) => serialNumbers[i] || '');
      setSerialNumbers(newSerialNumbers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.quantity, product.isSerialized]);

  const fetchWarehouses = async () => {
    try {
      const response = await api.get('/warehouses');
      if (response.data.success) {
        setWarehouses(response.data.data);
        if (response.data.data.length > 0) {
          setFormData(prev => ({ ...prev, warehouseId: response.data.data[0].id }));
        }
      }
    } catch (err: any) {
      console.error('Error fetching warehouses:', err);
      setError('Failed to load warehouses');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'quantity' || name === 'unitCost' ? parseFloat(value) || 0 : value,
    }));
  };

  const handleSerialNumberChange = (index: number, value: string) => {
    const newSerialNumbers = [...serialNumbers];
    newSerialNumbers[index] = value;
    setSerialNumbers(newSerialNumbers);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.warehouseId) { setError('Please select a warehouse'); return; }
    if (formData.quantity <= 0) { setError('Quantity must be greater than 0'); return; }

    if (product.isSerialized) {
      const filledSerials = serialNumbers.filter(s => s.trim() !== '');
      if (filledSerials.length !== formData.quantity) {
        setError(`Please enter all ${formData.quantity} serial numbers`);
        return;
      }
      const uniqueSerials = new Set(serialNumbers);
      if (uniqueSerials.size !== serialNumbers.length) {
        setError('Duplicate serial numbers detected');
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        productId: product.id,
        warehouseId: formData.warehouseId,
        quantity: formData.quantity,
        unitCost: formData.unitCost,
        referenceType: formData.referenceType || undefined,
        referenceId: formData.referenceId || undefined,
        notes: formData.notes || undefined,
        serialNumbers: product.isSerialized ? serialNumbers : undefined,
      };
      const response = await api.post('/stock-movements/receive', payload);
      if (response.data.success) {
        onSuccess();
        resetForm();
        onClose();
      }
    } catch (err: any) {
      console.error('Error receiving stock:', err);
      setError(err.response?.data?.message || 'Failed to receive stock');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ warehouseId: warehouses[0]?.id || '', quantity: 1, unitCost: product.costPrice || 0, referenceType: '', referenceId: '', notes: '' });
    setSerialNumbers(['']);
    setError('');
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(7,7,15,0.85)',
      overflowY: 'auto', zIndex: 50, display: 'flex',
      alignItems: 'flex-start', justifyContent: 'center',
      paddingTop: 80, paddingBottom: 40,
    }}>
      <div style={{
        background: '#ffffff', border: '1px solid rgba(92,78,51,0.25)',
        borderRadius: 8, width: '100%', maxWidth: 600, padding: '28px 32px',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: 20, paddingBottom: 16, borderBottom: '1px solid rgba(92,78,51,0.15)',
        }}>
          <h3 style={{ fontFamily: "'Inter', sans-serif", fontSize: 22, fontWeight: 300, color: '#1c2033', margin: 0 }}>
            Receive Stock
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
              color: '#6b6a5c', cursor: 'pointer', padding: '4px 8px', fontSize: 16, lineHeight: 1,
            }}
          >
            ✕
          </button>
        </div>

        {/* Product Info Panel */}
        <div style={{
          background: 'rgba(241,245,249,1)', border: '1px solid rgba(92,78,51,0.22)',
          borderRadius: 6, padding: '10px 14px', marginBottom: 20,
        }}>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52', margin: 0 }}>
            <span style={{ color: '#d97706', fontWeight: 500 }}>Product:</span> {product.name}{' '}
            <span style={{ color: '#6b6a5c' }}>({product.sku})</span>
          </p>
          {product.isSerialized && (
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52', margin: '4px 0 0' }}>
              <span style={{ color: '#d97706', fontWeight: 500 }}>Type:</span> Serialized Item — Serial numbers required
            </p>
          )}
        </div>

        {/* Error */}
        {error && (
          <div style={{
            background: 'rgba(201,90,90,0.08)', border: '1px solid rgba(201,90,90,0.2)',
            borderRadius: 6, padding: '10px 14px', marginBottom: 16,
          }}>
            <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#c95a5a', margin: 0 }}>{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Warehouse */}
          <div>
            <label style={labelStyle}>Warehouse <span style={{ color: '#c95a5a' }}>*</span></label>
            <select
              name="warehouseId"
              value={formData.warehouseId}
              onChange={handleInputChange}
              style={inputStyle}
              required
            >
              <option value="">Select Warehouse</option>
              {warehouses.map(warehouse => (
                <option key={warehouse.id} value={warehouse.id}>
                  {warehouse.name} ({warehouse.code})
                </option>
              ))}
            </select>
          </div>

          {/* Quantity and Unit Cost */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>Quantity <span style={{ color: '#c95a5a' }}>*</span></label>
              <input
                type="number"
                name="quantity"
                value={formData.quantity}
                onChange={handleInputChange}
                min="1"
                step="1"
                style={inputStyle}
                required
              />
            </div>
            <div>
              <label style={labelStyle}>Unit Cost</label>
              <input
                type="number"
                name="unitCost"
                value={formData.unitCost}
                onChange={handleInputChange}
                min="0"
                step="0.01"
                style={inputStyle}
              />
            </div>
          </div>

          {/* Serial Numbers */}
          {product.isSerialized && (
            <div>
              <label style={labelStyle}>Serial Numbers <span style={{ color: '#c95a5a' }}>*</span></label>
              <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginBottom: 8, marginTop: 0 }}>
                Enter {formData.quantity} unique serial number(s)
              </p>
              <div style={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {serialNumbers.map((serial, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', width: 24, textAlign: 'right', flexShrink: 0 }}>
                      {index + 1}.
                    </span>
                    <input
                      type="text"
                      value={serial}
                      onChange={(e) => handleSerialNumberChange(index, e.target.value)}
                      placeholder={`Serial #${index + 1}`}
                      style={{ ...inputStyle, flex: 1 }}
                      required
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reference Information */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label style={labelStyle}>Reference Type</label>
              <select
                name="referenceType"
                value={formData.referenceType}
                onChange={handleInputChange}
                style={inputStyle}
              >
                <option value="">None</option>
                <option value="BILL">Bill</option>
                <option value="INVOICE">Invoice</option>
                <option value="TRANSFER">Transfer</option>
                <option value="ADJUSTMENT">Adjustment</option>
                <option value="MANUAL">Manual</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Reference ID</label>
              <input
                type="text"
                name="referenceId"
                value={formData.referenceId}
                onChange={handleInputChange}
                placeholder="e.g., BILL-001"
                style={inputStyle}
              />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label style={labelStyle}>Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={3}
              placeholder="Add any notes about this stock receipt..."
              style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
            />
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 16, borderTop: '1px solid rgba(92,78,51,0.15)' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '10px 24px', background: 'none',
                border: '1px solid rgba(92,78,51,0.25)', borderRadius: 4,
                fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 400,
                letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6b6a5c', cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px 24px',
                background: loading ? 'rgba(92,78,51,0.25)' : 'linear-gradient(135deg, #d97706, #b45309)',
                border: 'none', borderRadius: 4,
                fontFamily: "'Inter', sans-serif", fontSize: 9, fontWeight: 500,
                letterSpacing: '0.15em', textTransform: 'uppercase',
                color: loading ? '#c9c2ab' : '#1c2033',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Receiving...' : 'Receive Stock'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReceiveStockModal;
